<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="1" />
    <h4>Trámites</h4>
    <hr />

    <!-- Selector registros por página -->
    <div class="d-flex justify-content-end align-items-center gap-2 mb-2" v-if="tramites.length > 0">
      <label class="form-label mb-0 small fw-bold">Mostrar</label>
      <select v-model.number="elementosPorPagina" class="form-select form-select-sm"
        style="width: 75px;" @change="aplicarPaginacion(1)">
        <option :value="10">10</option>
        <option :value="25">25</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
      </select>
      <span class="small text-muted">registros</span>
    </div>

    <!-- Tabla -->
    <div class="table-responsive" v-if="tramites.length > 0">
      <table class="table table-hover table-bordered table-sm" id="sampleTable">
        <thead class="table-dark">
          <tr>
            <th class="th-sort" @click="ordenarPor('idTramite')">
              Número <span>{{ iconOrden('idTramite') }}</span>
            </th>
            <th class="th-sort" @click="ordenarPor('servicioNombre')">
              Servicio <span>{{ iconOrden('servicioNombre') }}</span>
            </th>
            <th class="th-sort" @click="ordenarPor('descripcion')">
              Descripción <span>{{ iconOrden('descripcion') }}</span>
            </th>
            <th class="th-sort" @click="ordenarPor('fechaDesde')">
              Fecha desde <span>{{ iconOrden('fechaDesde') }}</span>
            </th>
            <th class="th-sort" @click="ordenarPor('fechaHasta')">
              Fecha hasta <span>{{ iconOrden('fechaHasta') }}</span>
            </th>
            <th class="th-sort" @click="ordenarPor('estadoTramite')">
              Estado <span>{{ iconOrden('estadoTramite') }}</span>
            </th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="tramite in datosPaginados"
            :key="tramite.idTramite"
            @dblclick="handleRowClick($event, tramite.idTramite)"
            title="Doble clic para ver detalles"
          >
            <td>{{ tramite.idTramite }}</td>
            <td>{{ tramite.servicioNombre }}</td>
            <td>{{ tramite.descripcion }}</td>
            <td>{{ getFormattedDate(tramite.fechaDesde) }}</td>
            <td>{{ getFormattedDate(tramite.fechaHasta) }}</td>
            <td>
              <span class="badge" :class="badgeEstado(tramite.estadoTramite)">
                {{ tramite.estadoTramite }}
              </span>
            </td>
            <td>
              <select class="form-select form-select-sm"
                @change="handleAction($event, tramite.idTramite)">
                <option disabled selected>Seleccione...</option>
                <option value="observaciones">Observaciones</option>
                <option value="menuObservaciones">Menu Observaciones</option>
              </select>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="table-secondary">
            <td colspan="7" class="text-end fw-bold small">
              Mostrando {{ datosPaginados.length }} de {{ tramitesOrdenados.length }} trámites
            </td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- Sin resultados -->
    <div v-if="sinResultados" class="alert alert-warning mt-3" role="alert">
      No se recuperaron registros.
    </div>

    <!-- Paginación -->
    <div class="d-flex justify-content-between align-items-center mt-3" v-if="tramites.length > 0">
      <span class="small text-muted">
        Página {{ paginaActual }} de {{ totalPaginas() }} — {{ tramitesOrdenados.length }} registros
      </span>
      <nav aria-label="Paginación">
        <ul class="pagination pagination-sm mb-0">
          <li class="page-item" :class="{ disabled: paginaActual === 1 }">
            <a class="page-link" href="#" @click.prevent="aplicarPaginacion(1)">«</a>
          </li>
          <li class="page-item" :class="{ disabled: paginaActual === 1 }">
            <a class="page-link" href="#" @click.prevent="getPreviousPage()">‹</a>
          </li>
          <li v-for="pagina in paginasVisibles" :key="pagina"
            class="page-item"
            :class="{ active: pagina === paginaActual, disabled: pagina === '...' }">
            <a class="page-link" href="#"
              @click.prevent="pagina !== '...' && aplicarPaginacion(pagina)">
              {{ pagina }}
            </a>
          </li>
          <li class="page-item" :class="{ disabled: paginaActual === totalPaginas() }">
            <a class="page-link" href="#" @click.prevent="getNextPage()">›</a>
          </li>
          <li class="page-item" :class="{ disabled: paginaActual === totalPaginas() }">
            <a class="page-link" href="#" @click.prevent="aplicarPaginacion(totalPaginas())">»</a>
          </li>
        </ul>
      </nav>
    </div>

    <Footer />
  </div>
</template>

<script>
import Layout from "@/components/Layout.vue";
import Footer from "@/components/Footer.vue";
import api from "@/services/api";
import dateFormat from "@/modules/dateFormat.js";

export default {
  components: { Layout, Footer },
  data() {
    return {
      tramites: [],
      elementosPorPagina: 10,
      datosPaginados: [],
      paginaActual: 1,
      p_idPrestador: 0,
      idEmpresa: 0,
      sinResultados: false,
      campoOrden: 'idTramite',
      ascendente: false,
    };
  },
  computed: {
    tramitesOrdenados() {
      const campo = this.campoOrden;
      return [...this.tramites].sort((a, b) => {
        let va = a[campo] ?? '', vb = b[campo] ?? '';
        if (campo === 'idTramite') {
          const na = parseInt(va, 10), nb = parseInt(vb, 10);
          if (!isNaN(na) && !isNaN(nb)) return this.ascendente ? na - nb : nb - na;
        }
        if (campo === 'fechaDesde' || campo === 'fechaHasta') {
          const da = new Date(va), db = new Date(vb);
          if (!isNaN(da) && !isNaN(db)) return this.ascendente ? da - db : db - da;
        }
        const cmp = String(va).localeCompare(String(vb), 'es');
        return this.ascendente ? cmp : -cmp;
      });
    },
    paginasVisibles() {
      const total = this.totalPaginas(), actual = this.paginaActual, delta = 2;
      const rango = [];
      for (let i = Math.max(2, actual - delta); i <= Math.min(total - 1, actual + delta); i++) {
        rango.push(i);
      }
      if (rango.length && rango[0] > 2) rango.unshift('...');
      if (rango.length && rango[rango.length - 1] < total - 1) rango.push('...');
      const paginas = [1];
      rango.forEach(p => paginas.push(p));
      if (total > 1) paginas.push(total);
      return paginas;
    },
  },
  mounted() {
    this.idEmpresa     = window.sessionStorage.getItem("idEmpresa");
    this.p_idPrestador = window.sessionStorage.getItem("idPrestador");
    this.getDataPaginaTodos(this.paginaActual);
    this.$refs.layoutRef.toggleProcesos();
    this.$refs.layoutRef.toggleReportes();
    this.$refs.layoutRef.toggleParametros();
    this.$refs.layoutRef.toggleAyuda();
  },
  methods: {
    ordenarPor(campo) {
      this.ascendente = campo === this.campoOrden ? !this.ascendente : true;
      this.campoOrden = campo;
      this.aplicarPaginacion(1);
    },
    iconOrden(campo) {
      if (this.campoOrden !== campo) return '⇅';
      return this.ascendente ? '↑' : '↓';
    },
    badgeEstado(estado) {
      return {
        'bg-secondary':           ['REGISTRADO','CARGADO','ESTRUCTURADO','DOCUMENTADO','ENVIADO'].includes(estado),
        'bg-primary':             estado === 'RECIBIDO',
        'bg-warning text-dark':   ['EN PROCESO','PLANILLAJE'].includes(estado),
        'bg-info text-dark':      estado === 'OBSERVACIONES',
        'bg-success':             estado === 'FINALIZADO',
      };
    },
    totalPaginas() {
      return Math.max(1, Math.ceil(this.tramitesOrdenados.length / this.elementosPorPagina));
    },
    aplicarPaginacion(noPagina) {
      this.paginaActual = noPagina;
      const ini = (noPagina - 1) * this.elementosPorPagina;
      this.datosPaginados = this.tramitesOrdenados.slice(ini, ini + this.elementosPorPagina);
    },
    getPreviousPage() {
      if (this.paginaActual > 1) this.aplicarPaginacion(this.paginaActual - 1);
    },
    getNextPage() {
      if (this.paginaActual < this.totalPaginas()) this.aplicarPaginacion(this.paginaActual + 1);
    },
    getFormattedDate(date) { return dateFormat.getFormattedDate(date); },
    setPaginacion(data, noPagina) {
      this.tramites = Array.isArray(data) ? data : [];
      this.aplicarPaginacion(noPagina);
      this.sinResultados = this.tramites.length === 0;
    },
    getDataPaginaTodos(noPagina) {
      api.get(`/vwtramites/idempresa-idprestador-estado/${this.idEmpresa}/${this.p_idPrestador}/EN PROCESO`)
        .then(r => this.setPaginacion(r.data, noPagina));
    },
handleRowClick(event, idTramite) {
  if (event.target.tagName === 'SELECT' || event.target.tagName === 'OPTION') return;
  this.abrirEditar(idTramite);
},
handleAction(event, idTramite) {
  const action = event.target.value;
  if (action === "observaciones") this.abrirEditar(idTramite);
  if (action === "menuObservaciones") this.abrirMenuEditar(idTramite);
  event.target.value = "Seleccione...";
},
    abrirEditar(idTramite) {
      if (idTramite != null) this.$router.push("/planillas-observaciones/" + idTramite);
    },
    abrirMenuEditar(idTramite) {
      if (idTramite != null) this.$router.push("/planillas-menu-observaciones/" + idTramite);
    },
  },
};
</script>

<style scoped>
.th-sort {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.th-sort:hover {
  background-color: #3d5166;
}
.table-hover tbody tr {
  cursor: pointer;
}
</style>